import React from 'react';
import { Box, Typography, Chip, Stack, SxProps, Theme as MuiTheme } from '@mui/material';
import { Check as CheckIcon, Lock as LockIcon } from '@mui/icons-material';
import type { ThemeData, ThemeWithOwnership } from '../../types';
import { DEFAULT_THEME_DATA } from '../../types';

interface ThemePreviewProps {
  theme: ThemeWithOwnership;
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  showOwnershipBadge?: boolean;
  /** When true, shows "Free" or "Premium" badge for all owned themes (used in My Themes view) */
  isMyThemesView?: boolean;
  /** When true, hides the name and badges below the preview grid (for use in cards that show their own info) */
  hideInfo?: boolean;
  size?: 'small' | 'medium' | 'large';
  sx?: SxProps<MuiTheme>;
}

// Sample data for the mini grid preview
const PREVIEW_NUMBERS_HOME = [0, 3, 7];
const PREVIEW_NUMBERS_AWAY = [4, 1, 8];

// Simulated square states for preview
const PREVIEW_STATES: ('unclaimed' | 'claimed_other' | 'claimed_self' | 'winner')[][] = [
  ['claimed_other', 'claimed_self', 'unclaimed'],
  ['winner', 'claimed_other', 'claimed_self'],
  ['unclaimed', 'unclaimed', 'claimed_other'],
];

const getSizeConfig = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return { gridSize: 80, cellSize: 20, fontSize: '0.5rem', headerSize: 14 };
    case 'large':
      return { gridSize: 180, cellSize: 48, fontSize: '0.75rem', headerSize: 28 };
    default:
      return { gridSize: 120, cellSize: 32, fontSize: '0.625rem', headerSize: 20 };
  }
};

const getSquareColor = (state: string, themeData: ThemeData) => {
  switch (state) {
    case 'winner':
      return themeData.squareWinner;
    case 'claimed_self':
      return themeData.squareClaimedSelf;
    case 'claimed_other':
      return themeData.squareClaimedOther;
    default:
      return themeData.squareUnclaimed;
  }
};

export const ThemePreview: React.FC<ThemePreviewProps> = ({
  theme,
  selected = false,
  onClick,
  disabled = false,
  showOwnershipBadge = true,
  isMyThemesView = false,
  hideInfo = false,
  size = 'medium',
  sx,
}) => {
  const themeData = theme.themeData || DEFAULT_THEME_DATA;
  const config = getSizeConfig(size);
  // In My Themes view, never show lock since user owns all shown themes
  const isLocked = !isMyThemesView && !theme.isFree && !theme.owned;

  return (
    <Box
      onClick={!disabled ? onClick : undefined}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: 2,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'action.selected' : 'background.paper',
        cursor: disabled ? 'not-allowed' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s ease',
        '&:hover': onClick && !disabled ? {
          borderColor: 'primary.light',
          bgcolor: 'action.hover',
        } : {},
        ...sx,
      }}
    >
      {/* Mini Grid Preview */}
      <Box
        sx={{
          position: 'relative',
          width: config.gridSize + config.headerSize,
          height: config.gridSize + config.headerSize,
          bgcolor: themeData.gridBackground,
          borderRadius: 1,
          overflow: 'hidden',
          mb: 1.5,
          mx: 'auto',
        }}
      >
        {/* Away Team Header (top row) */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: config.headerSize,
            right: 0,
            height: config.headerSize,
            display: 'flex',
            bgcolor: themeData.awayTeamColor,
          }}
        >
          {PREVIEW_NUMBERS_AWAY.map((num, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: themeData.awayTeamAccent,
                fontSize: config.fontSize,
                fontWeight: themeData.headerFontWeight,
                fontFamily: themeData.fontFamily,
              }}
            >
              {num}
            </Box>
          ))}
        </Box>

        {/* Home Team Header (left column) */}
        <Box
          sx={{
            position: 'absolute',
            top: config.headerSize,
            left: 0,
            width: config.headerSize,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: themeData.homeTeamColor,
          }}
        >
          {PREVIEW_NUMBERS_HOME.map((num, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: themeData.homeTeamAccent,
                fontSize: config.fontSize,
                fontWeight: themeData.headerFontWeight,
                fontFamily: themeData.fontFamily,
              }}
            >
              {num}
            </Box>
          ))}
        </Box>

        {/* Grid Cells */}
        <Box
          sx={{
            position: 'absolute',
            top: config.headerSize,
            left: config.headerSize,
            right: 0,
            bottom: 0,
            display: 'grid',
            gridTemplateColumns: `repeat(3, 1fr)`,
            gridTemplateRows: `repeat(3, 1fr)`,
            gap: `${themeData.cellBorderWidth}px`,
            bgcolor: themeData.cellBorder,
          }}
        >
          {PREVIEW_STATES.flat().map((state, i) => {
            const isWinner = state === 'winner';
            return (
              <Box
                key={i}
                sx={{
                  bgcolor: getSquareColor(state, themeData),
                  borderRadius: `${themeData.cellBorderRadius}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                {isWinner && (
                  <Box
                    sx={{
                      width: '60%',
                      height: '60%',
                      borderRadius: '50%',
                      bgcolor: themeData.winnerBadgeBackground,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        color: themeData.winnerBadgeTextColor,
                        fontSize: `calc(${config.fontSize} * 0.8)`,
                        fontWeight: 'bold',
                      }}
                    >
                      ★
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Lock overlay for non-owned premium themes */}
        {isLocked && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(0, 0, 0, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
            }}
          >
            <LockIcon sx={{ color: 'white', fontSize: config.gridSize * 0.3 }} />
          </Box>
        )}
      </Box>

      {/* Theme Info - only show if hideInfo is false */}
      {!hideInfo && (
        <Stack spacing={0.5} alignItems="center">
          <Typography
            variant="subtitle2"
            fontWeight={600}
            noWrap
            sx={{ maxWidth: '100%' }}
          >
            {theme.name}
          </Typography>

          {/* Badges - simplified for My Themes view */}
          <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center">
            {isMyThemesView ? (
              // In My Themes view: show single badge with pricing type
              <Chip
                label={theme.isFree ? 'Free' : 'Premium'}
                size="small"
                color={theme.isFree ? 'success' : 'primary'}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            ) : (
              // In other views: show Free or Owned badges
              <>
                {theme.isFree && (
                  <Chip
                    label="Free"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                )}
                {showOwnershipBadge && theme.owned && !theme.isFree && (
                  <Chip
                    icon={<CheckIcon sx={{ fontSize: '0.75rem !important' }} />}
                    label="Owned"
                    size="small"
                    color="primary"
                    variant="filled"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                )}
              </>
            )}
            {selected && (
              <Chip
                icon={<CheckIcon sx={{ fontSize: '0.75rem !important' }} />}
                label="Selected"
                size="small"
                color="primary"
                variant="filled"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            )}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default ThemePreview;
